import React, { memo, useCallback, useMemo } from "react";
import { MultiValue, Select } from "chakra-react-select";
import { InternalAdminDistributionGroupFragment } from "gql/graphql";
import colors from "app/colors";
import { SystemStyleObject } from "@chakra-ui/react";
import DistributionGroupOptionLabel from "modules/selectors/distribution-group-option-label";
import useInternalAdminDistributionGroupList from "../hooks/use-internal-admin-distribution-group-list";
import useQueryString from "modules/hooks/use-query-string";

interface IProps {
  isDisabled?: boolean;
  onChange: (value: InternalAdminDistributionGroupFragment[]) => void;
  excludeDistributionGroupIds?: string[];
}

const InternalAdminDistributionGroupSelect = ({
  isDisabled = false,
  onChange,
  excludeDistributionGroupIds,
}: IProps) => {
  const { qsOrganizationId } = useQueryString();

  const { distributionGroups } = useInternalAdminDistributionGroupList({
    organizationId: qsOrganizationId,
    input: {
      limit: 1000000,
      offset: 0,
    },
  });

  const options = useMemo(
    () =>
      distributionGroups.filter(
        (distributionGroup) =>
          !(excludeDistributionGroupIds ?? []).includes(distributionGroup.id),
      ),
    [excludeDistributionGroupIds, distributionGroups],
  );

  const _onChange = useCallback(
    (option: MultiValue<InternalAdminDistributionGroupFragment>) => {
      onChange([...option]);
    },
    [onChange],
  );

  const formatOptionLabel = useCallback(
    (option: InternalAdminDistributionGroupFragment) => (
      <DistributionGroupOptionLabel distributionGroup={option} search="" />
    ),
    [],
  );

  const getOptionLabel = useCallback(
    (option: InternalAdminDistributionGroupFragment) => option.name,
    [],
  );

  const getOptionValue = useCallback(
    (option: InternalAdminDistributionGroupFragment) => option.id,
    [],
  );

  const isEmpty = options.length === 0;

  return (
    <Select
      options={options}
      isDisabled={isDisabled || isEmpty}
      isClearable
      isMulti
      formatOptionLabel={formatOptionLabel}
      getOptionLabel={getOptionLabel}
      getOptionValue={getOptionValue}
      onChange={_onChange}
      components={{
        DropdownIndicator: () => null,
        IndicatorSeparator: () => null,
      }}
      chakraStyles={{
        container: (provided: SystemStyleObject): SystemStyleObject => ({
          ...provided,
          width: "full",
          borderColor: colors.grayPrimary,
        }),
        placeholder: (provided: SystemStyleObject): SystemStyleObject => ({
          ...provided,
          fontStyle: "italic",
          color: colors.placeholderText,
        }),
        multiValue: (provided: SystemStyleObject): SystemStyleObject => ({
          ...provided,
          borderRadius: "60px",
          borderWidth: "1px",
          borderColor: colors.blue,
          backgroundColor: colors.white,
        }),
        multiValueRemove: (provided: SystemStyleObject): SystemStyleObject => ({
          ...provided,
          color: colors.blue,
        }),
      }}
      placeholder={
        isEmpty
          ? "No distribution groups available"
          : "Select a distribution group..."
      }
    />
  );
};

export default memo(InternalAdminDistributionGroupSelect);
